/* Listing dropdowns retain their native select as the source of values and change events. */
(() => {
  if (customElements.get('listing-select')) return;
  let sequence = 0;
  let opened = null;

  class ListingSelect extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('select')) this.setup();
      else queueMicrotask(() => { if (this.isConnected) this.setup(); });
    }
    setup() {
      if (this.controller) return;
      this.select = this.querySelector('select');
      if (!this.select || this.select.multiple) return;
      this.controller = new AbortController();
      const events = { signal: this.controller.signal };
      this.originalTabindex = this.select.getAttribute('tabindex');
      this.originalHidden = this.select.getAttribute('aria-hidden');
      this.select.tabIndex = -1;
      this.select.setAttribute('aria-hidden', 'true');
      this.classList.add('listing-select');
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'listing-select__trigger';
      this.button.setAttribute('role', 'combobox');
      this.button.setAttribute('aria-haspopup', 'listbox');
      this.button.setAttribute('aria-expanded', 'false');
      this.valueLabel = document.createElement('span');
      this.valueLabel.className = 'listing-select__value';
      this.button.append(this.valueLabel);
      this.list = document.createElement('div');
      this.list.id = 'listing-select-options-' + ++sequence;
      this.list.className = 'listing-select__list';
      this.list.setAttribute('role', 'listbox');
      this.list.hidden = true;
      // A top-layer popover escapes the Bundle panes' independent scroll clipping.
      this.hasPopover = typeof this.list.showPopover === 'function';
      if (this.hasPopover) this.list.setAttribute('popover', 'manual');
      this.button.setAttribute('aria-controls', this.list.id);
      this.append(this.button, this.list);
      this.classList.add('listing-select--enhanced');
      this.button.addEventListener('click', event => {
        event.preventDefault();
        this.isOpen ? this.close() : this.open();
      }, events);
      this.button.addEventListener('keydown', event => this.onKeydown(event), events);
      this.list.addEventListener('click', event => {
        const option = event.target.closest('[data-select-index]');
        event.preventDefault();
        this.button.focus({ preventScroll: true });
        if (option) this.choose(Number(option.dataset.selectIndex));
      }, events);
      this.select.addEventListener('change', () => this.refresh(), events);
      this.select.addEventListener('focus', () => this.button.focus({ preventScroll: true }), events);
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this.select, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'selected', 'label', 'value', 'aria-label'] });
      this.refresh();
    }
    disconnectedCallback() {
      if (!this.controller) return;
      this.close();
      this.observer.disconnect();
      this.controller.abort();
      this.controller = null;
      this.button.remove();
      this.list.remove();
      this.classList.remove('listing-select--enhanced');
      for (const [attribute, value] of [['tabindex', this.originalTabindex], ['aria-hidden', this.originalHidden]]) {
        if (value === null) this.select.removeAttribute(attribute);
        else this.select.setAttribute(attribute, value);
      }
      this.optionSignature = null;
    }
    refresh() {
      if (!this.controller) return;
      const options = [...this.select.options];
      const name = this.select.getAttribute('aria-label') || this.select.closest('label')?.querySelector('.listing-sr-only')?.textContent.trim() || 'Choose an option';
      this.button.setAttribute('aria-label', name);
      this.list.setAttribute('aria-label', name);
      const value = this.select.selectedOptions[0]?.label || '';
      if (this.valueLabel.textContent !== value) this.valueLabel.textContent = value;
      this.button.title = value;
      this.button.disabled = this.select.disabled || !options.length;
      const signature = JSON.stringify(options.map(option => [option.label, option.value, option.disabled, option.hidden]));
      if (signature !== this.optionSignature) {
        this.optionSignature = signature;
        this.list.replaceChildren(...options.map((option, index) => {
          const item = document.createElement('div');
          item.className = 'listing-select__option';
          item.id = this.list.id + '-' + index;
          item.dataset.selectIndex = index;
          item.setAttribute('role', 'option');
          item.setAttribute('aria-disabled', String(option.disabled));
          item.hidden = option.hidden;
          item.textContent = option.label;
          return item;
        }));
      }
      [...this.list.children].forEach((item, index) => item.setAttribute('aria-selected', String(index === this.select.selectedIndex)));
      if (this.button.disabled) this.close();
      else if (this.isOpen) this.activate(this.select.selectedIndex);
    }
    open() {
      this.refresh();
      if (this.button.disabled || this.isOpen) return;
      opened?.close();
      this.isOpen = true;
      opened = this;
      this.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      this.list.hidden = false;
      if (this.hasPopover) this.list.showPopover();
      this.positionList();
      this.activate(this.select.selectedIndex);
    }
    close(restoreFocus = false) {
      if (!this.isOpen) return;
      this.isOpen = false;
      if (opened === this) opened = null;
      this.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
      this.button.removeAttribute('aria-activedescendant');
      if (this.hasPopover && this.list.matches(':popover-open')) this.list.hidePopover();
      this.list.hidden = true;
      this.search = '';
      if (restoreFocus && this.isConnected) this.button.focus({ preventScroll: true });
    }
    positionList() {
      const rect = this.button.getBoundingClientRect();
      const viewport = window.visualViewport;
      const left = viewport?.offsetLeft || 0;
      const top = viewport?.offsetTop || 0;
      const width = viewport?.width || innerWidth;
      const height = viewport?.height || innerHeight;
      const gap = 8, edge = 12;
      const below = top + height - edge - rect.bottom - gap;
      const above = rect.top - top - edge - gap;
      const upwards = below < Math.min(240, this.list.scrollHeight) && above > below;
      const available = Math.max(40, upwards ? above : below);
      const minimumWidth = parseFloat(getComputedStyle(this).getPropertyValue('--select-menu-min-width')) || 0;
      const listWidth = Math.min(Math.max(rect.width, minimumWidth), width - edge * 2);
      this.list.style.width = listWidth + 'px';
      this.list.style.maxHeight = Math.min(280, available) + 'px';
      this.list.style.left = Math.max(left + edge, Math.min(rect.right - listWidth, left + width - edge - listWidth)) + 'px';
      this.list.style.top = (upwards ? rect.top - gap - this.list.getBoundingClientRect().height : rect.bottom + gap) + 'px';
    }
    onScroll() {
      const rect = this.button.getBoundingClientRect();
      const pane = this.closest('[data-bundle-catalog], [data-bundle-summary]');
      const bounds = pane?.getBoundingClientRect();
      if (!rect.width || rect.bottom <= 0 || rect.top >= innerHeight || (bounds && (rect.bottom <= bounds.top || rect.top >= bounds.bottom))) this.close();
      else this.positionList();
    }
    activate(index) {
      const options = [...this.select.options];
      if (index < 0 || options[index]?.disabled || options[index]?.hidden) index = options.findIndex(option => !option.disabled && !option.hidden);
      this.activeIndex = index;
      [...this.list.children].forEach((item, i) => item.classList.toggle('is-active', i === index));
      const item = this.list.children[index];
      if (!item) { this.button.removeAttribute('aria-activedescendant'); return; }
      this.button.setAttribute('aria-activedescendant', item.id);
      // Scroll only the options, never the page or the containing Bundle pane.
      if (item.offsetTop < this.list.scrollTop) this.list.scrollTop = item.offsetTop;
      else if (item.offsetTop + item.offsetHeight > this.list.scrollTop + this.list.clientHeight) this.list.scrollTop = item.offsetTop + item.offsetHeight - this.list.clientHeight;
    }
    choose(index) {
      const option = this.select.options[index];
      if (!option || option.disabled || option.hidden || this.select.disabled) return;
      const changed = this.select.selectedIndex !== index;
      this.select.selectedIndex = index;
      this.refresh();
      this.close(true);
      if (changed) {
        this.select.dispatchEvent(new Event('input', { bubbles: true }));
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    onKeydown(event) {
      const options = [...this.select.options];
      const enabled = options.map((option, index) => !option.disabled && !option.hidden ? index : -1).filter(index => index >= 0);
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const wasOpen = this.isOpen;
        if (!wasOpen) this.open();
        let position = enabled.indexOf(this.activeIndex);
        if (event.key === 'Home') position = 0;
        else if (event.key === 'End') position = enabled.length - 1;
        else if (wasOpen) position = Math.max(0, Math.min(enabled.length - 1, position + (event.key === 'ArrowDown' ? 1 : -1)));
        this.activate(enabled[position] ?? -1);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (this.isOpen) this.choose(this.activeIndex);
        else this.open();
      } else if (event.key === 'Tab') this.close();
      else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        if (!this.isOpen) this.open();
        const now = Date.now();
        this.search = (now - (this.searchTime || 0) < 700 ? this.search || '' : '') + event.key.toLowerCase();
        this.searchTime = now;
        const match = enabled.find(index => options[index].label.toLowerCase().startsWith(this.search));
        if (match !== undefined) this.activate(match);
      }
    }
  }
  customElements.define('listing-select', ListingSelect);
  document.addEventListener('pointerdown', event => { if (opened && !opened.contains(event.target)) opened.close(); }, true);
  document.addEventListener('keydown', event => {
    if (opened && event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      opened.close(true);
    }
  }, true);
  document.addEventListener('scroll', event => { if (opened && !opened.list.contains(event.target)) opened.onScroll(); }, true);
  document.addEventListener('close', event => { if (opened?.closest('dialog') === event.target) opened.close(); }, true);
  window.addEventListener('resize', () => opened?.close());
  window.visualViewport?.addEventListener('resize', () => opened?.close());
})();
